import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';

import { HolidayPeriodDtoIn } from '@/models/in/HolidayPeriodDtoIn';
import DataTable from '@/components/ui/DataTable/DataTable';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import useTableDensity from '@/hooks/useTableDensity';
import EditOrCreateHolidayPeriodDialog from './EditOrCreateHolidayPeriodDialog';
import DeleteHolidayPeriodDialog from './DeleteHolidayPeriodDialog';

const QUERY_URL = 'management/holiday-periods';
const DENSITY_STORAGE_KEY = 'table.density.holidayPeriods';

const HolidayPeriodsManagementPage = () => {
  const { t } = useTranslation();

  const [isEditPeriodDialogOpen, setIsEditPeriodDialogOpen] = useState(false);
  const [periodToEdit, setPeriodToEdit] = useState<HolidayPeriodDtoIn | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState<HolidayPeriodDtoIn | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');

  const { data: holidayPeriods, isLoading, refetch } = useGetAuthApi<HolidayPeriodDtoIn[]>(QUERY_URL);

  const handleOpenCreatePeriodDialog = useCallback(() => {
    setPeriodToEdit(null);
    setIsEditPeriodDialogOpen(true);
  }, []);

  const handleOpenEditPeriodDialog = useCallback((period: HolidayPeriodDtoIn) => {
    setPeriodToEdit(period);
    setIsEditPeriodDialogOpen(true);
  }, []);

  const handleCloseEditPeriodDialog = useCallback(() => {
    setIsEditPeriodDialogOpen(false);
    setPeriodToEdit(null);
  }, []);

  const handleOpenDeleteDialog = useCallback((period: HolidayPeriodDtoIn) => {
    setPeriodToDelete(period);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setPeriodToDelete(null);
  }, []);

  const columns: ColumnDef<HolidayPeriodDtoIn>[] = [
    {
      id: 'name',
      header: Col(t('holidayPeriodsManagement.name')),
      accessorKey: 'name',
      enableSorting: true,
      meta: {
        className: 'min-w-[200px]',
      },
    },
    {
      id: 'startDate',
      header: Col(t('holidayPeriodsManagement.startDate')),
      accessorKey: 'startDate',
      cell: Cell(cellType.Date),
      enableSorting: true,
      meta: {
        className: 'min-w-[130px] max-w-[130px]',
      },
    },
    {
      id: 'endDate',
      header: Col(t('holidayPeriodsManagement.endDate')),
      accessorKey: 'endDate',
      cell: Cell(cellType.Date),
      enableSorting: true,
      meta: {
        className: 'min-w-[130px] max-w-[130px]',
      },
    },
    {
      id: 'actions',
      header: Col(t('holidayPeriodsManagement.actions')),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditPeriodDialog(row.original);
            }}
          >
            {t('holidayPeriodsManagement.edit')}
          </Button>
          <Button
            variant="outline"
            className="!text-red-500 !border-red-300"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDeleteDialog(row.original);
            }}
          >
            {t('holidayPeriodsManagement.delete')}
          </Button>
        </div>
      ),
      meta: {
        className: 'min-w-[200px]',
        lockLayout: true,
      },
    },
  ];

  return (
    <div>
      <DataTable
        title={t('holidayPeriodsManagement.title')}
        data={holidayPeriods ?? []}
        isLoading={isLoading}
        columns={columns}
        filters={
          <Button variant="primary" onClick={handleOpenCreatePeriodDialog}>
            {t('holidayPeriodsManagement.addPeriod')}
          </Button>
        }
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="holidayPeriods"
        searchPlaceholder="holidayPeriodsManagement.search"
        customGlobalSearch={() => true}
      />

      <EditOrCreateHolidayPeriodDialog
        open={isEditPeriodDialogOpen}
        onClose={handleCloseEditPeriodDialog}
        periodToEdit={periodToEdit}
        onSuccess={refetch}
      />

      {isDeleteDialogOpen && periodToDelete && (
        <DeleteHolidayPeriodDialog
          open={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          period={periodToDelete}
          onSuccess={refetch}
        />
      )}
    </div>
  );
};

export default HolidayPeriodsManagementPage;
